// @ts-strict-ignore
import { gql, useQuery } from "@apollo/client";
import React from "react";

import { useAnalyticsMode } from "../../../components/AnalyticsMode/AnalyticsModeProvider";
import { generateMockOrders } from "../mock/generateMockOrders";
import { AnalyticsResult, CategorySales,Order, TopCustomer, TopProduct } from "../models/analytics";
import { DateRange } from "../models/filters";
import { aggregateAnalytics } from "../utils/aggregate";

const ANALYTICS_ORDERS_QUERY = gql`
  query AnalyticsOrders($first: Int, $after: String, $filter: OrderFilterInput) {
    orders(first: $first, after: $after, filter: $filter) {
      edges {
        node {
          id
          number
          created
          status
          userEmail
          user { id email }
          channel { id name currencyCode }
          total { gross { amount currency } }
          totalRefunded { amount currency }
          shippingPrice { gross { amount currency } }
          discounts { type amount { amount currency } }
          fulfillments { created }
          lines {
            quantity
            totalPrice { gross { amount currency } }
            productName
            variant { sku product { id name category { id name } } }
          }
        }
      }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

const ANALYTICS_CHECKOUTS_COUNT_QUERY = gql`
  query AnalyticsCheckoutsCount($filter: CheckoutFilterInput) {
    checkouts(first: 1, filter: $filter) {
      totalCount
    }
  }
`;

// Lightweight summary used for compare-to-prior-period deltas.
// No lines / no customers / no fulfillments — payload kept small.
const ANALYTICS_ORDERS_SUMMARY_QUERY = gql`
  query AnalyticsOrdersSummary($first: Int, $after: String, $filter: OrderFilterInput) {
    orders(first: $first, after: $after, filter: $filter) {
      totalCount
      edges {
        node {
          id
          status
          total { gross { amount } }
          totalRefunded { amount }
          userEmail
          user { id }
        }
      }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

// Checkouts count for the prior period — funnel delta.
const ANALYTICS_CHECKOUTS_PRIOR_COUNT_QUERY = ANALYTICS_CHECKOUTS_COUNT_QUERY;

// Orders that are old + still not fulfilled — operational backlog tile.
const ANALYTICS_STUCK_ORDERS_QUERY = gql`
  query AnalyticsStuckOrders($filter: OrderFilterInput) {
    orders(first: 1, filter: $filter) {
      totalCount
    }
  }
`;

const ANALYTICS_VARIANTS_STOCKS = gql`
  query AnalyticsVariantsStocks($first: Int, $after: String) {
    productVariants(first: $first, after: $after) {
      edges {
        node {
          id
          sku
          name
          trackInventory
          stocks { quantity quantityAllocated }
        }
      }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

const ANALYTICS_ORDERS_ALL_CHANNELS = gql`
  query AnalyticsOrdersAllChannels($first: Int, $after: String, $filter: OrderFilterInput) {
    orders(first: $first, after: $after, filter: $filter) {
      edges {
        node {
          id
          channel { id name currencyCode }
          total { gross { amount currency } }
          totalRefunded { amount currency }
        }
      }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

type AnalyticsOrdersQueryResult = {
  orders?: {
    edges: Array<{
      node: {
        id: string;
        created: string;
        status: string;
        userEmail?: string | null;
        channel?: { id?: string | null } | null;
        total?: { gross?: { amount?: number; currency?: string } };
        lines?: Array<{
          quantity?: number;
          totalPrice?: { gross?: { amount?: number; currency?: string } };
          productName?: string | null;
          variant?: { product?: { id?: string; name?: string; category?: { id?: string; name?: string } | null } | null } | null;
        }>;
      };
    }>;
    pageInfo: { hasNextPage: boolean; endCursor?: string | null };
  };
};

type AnalyticsOrdersQueryVars = {
  first?: number;
  after?: string | null;
  filter?: any;
};

export const useAnalyticsData = (channelId: string | null | undefined, dateRange: DateRange) => {
  const { isLive } = useAnalyticsMode();

  // Build query variables regardless of mode; query will be skipped if not live
  const variables = React.useMemo(() => {
    const created = { gte: dateRange.from, lte: dateRange.to } as const;
    const filter: any = { created };

    if (channelId) {
      filter.channels = [channelId];
    }

    return {
      first: 100,
      filter,
    } as const;
  }, [channelId, dateRange]);

  // Second query to aggregate sales by channel across all channels for the date range
  const allChannelsVariables = React.useMemo(() => {
    const created = { gte: dateRange.from, lte: dateRange.to } as const;
    const filter: any = { created };

    return { first: 100, filter } as const;
  }, [dateRange]);

  // Prior-period date range (same length, immediately preceding).
  const priorRange = React.useMemo(() => {
    const from = new Date(dateRange.from + "T00:00:00Z");
    const to = new Date(dateRange.to + "T00:00:00Z");
    const lenDays =
      Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const priorTo = new Date(from);

    priorTo.setUTCDate(from.getUTCDate() - 1);

    const priorFrom = new Date(priorTo);

    priorFrom.setUTCDate(priorTo.getUTCDate() - (lenDays - 1));

    return {
      from: priorFrom.toISOString().slice(0, 10),
      to: priorTo.toISOString().slice(0, 10),
    };
  }, [dateRange]);

  const priorOrdersVariables = React.useMemo(() => {
    const created = { gte: priorRange.from, lte: priorRange.to } as const;
    const filter: any = { created };

    if (channelId) {
      filter.channels = [channelId];
    }

    return { first: 100, filter } as const;
  }, [channelId, priorRange]);

  const priorCheckoutsVariables = React.useMemo(() => {
    const created = { gte: priorRange.from, lte: priorRange.to } as const;

    return { filter: { created } } as const;
  }, [priorRange]);

  // Skip querying until we have a date range (always true) and channel id (required by decision)
  const skip = !isLive || !channelId;

  const { data: qData, loading: qLoading, fetchMore } = useQuery<AnalyticsOrdersQueryResult, AnalyticsOrdersQueryVars>(
    ANALYTICS_ORDERS_QUERY,
    {
      variables,
      skip,
      notifyOnNetworkStatusChange: true,
    },
  );

  const { data: allData, fetchMore: fetchMoreAll, loading: loadingAll } = useQuery<AnalyticsOrdersQueryResult, AnalyticsOrdersQueryVars>(
    ANALYTICS_ORDERS_ALL_CHANNELS,
    {
      variables: allChannelsVariables,
      skip: !isLive,
      notifyOnNetworkStatusChange: true,
    },
  );

  // Variants + stocks for inventory metrics
  type VariantsQueryResult = {
    productVariants?: {
      edges: Array<{ node: { id: string; sku?: string | null; name?: string | null; trackInventory?: boolean | null; stocks?: Array<{ quantity?: number | null; quantityAllocated?: number | null } | null> | null } }>;
      pageInfo: { hasNextPage: boolean; endCursor?: string | null };
    };
  };

  const { data: vData, fetchMore: fetchMoreVariants, loading: loadingVariants } = useQuery<VariantsQueryResult, { first?: number; after?: string | null }>(
    ANALYTICS_VARIANTS_STOCKS,
    {
      variables: { first: 100 },
      skip: !isLive,
      notifyOnNetworkStatusChange: true,
    },
  );

  // Checkouts in range — used only for the conversion-funnel "started" count.
  type CheckoutsCountResult = {
    checkouts?: { totalCount?: number | null } | null;
  };

  const checkoutsVariables = React.useMemo(() => {
    const created = { gte: dateRange.from, lte: dateRange.to } as const;

    return { filter: { created } } as const;
  }, [dateRange]);
  const { data: cData, loading: loadingCheckouts } = useQuery<CheckoutsCountResult, { filter: any }>(
    ANALYTICS_CHECKOUTS_COUNT_QUERY,
    {
      variables: checkoutsVariables,
      skip: !isLive,
      notifyOnNetworkStatusChange: true,
    },
  );

  // Prior period summary — orders count + GMV + refund + unique customers.
  type PriorOrdersSummary = {
    orders?: {
      totalCount?: number | null;
      edges: Array<{
        node: {
          id: string;
          status?: string | null;
          total?: { gross?: { amount?: number } | null } | null;
          totalRefunded?: { amount?: number } | null;
          userEmail?: string | null;
          user?: { id?: string | null } | null;
        };
      }>;
    };
  };

  const { data: priorData, loading: loadingPrior } = useQuery<PriorOrdersSummary, AnalyticsOrdersQueryVars>(
    ANALYTICS_ORDERS_SUMMARY_QUERY,
    {
      variables: priorOrdersVariables,
      skip: !isLive || !channelId,
      notifyOnNetworkStatusChange: true,
    },
  );

  const { data: priorCheckoutsData } = useQuery<CheckoutsCountResult, { filter: any }>(
    ANALYTICS_CHECKOUTS_PRIOR_COUNT_QUERY,
    {
      variables: priorCheckoutsVariables,
      skip: !isLive,
      notifyOnNetworkStatusChange: true,
    },
  );

  // Stuck orders: UNFULFILLED or PARTIALLY_FULFILLED, created more than 7 days ago.
  const STUCK_DAYS = 7;
  const stuckVariables = React.useMemo(() => {
    const cutoff = new Date();

    cutoff.setUTCDate(cutoff.getUTCDate() - STUCK_DAYS);

    const filter: any = {
      status: ["UNFULFILLED", "PARTIALLY_FULFILLED"],
      created: { lte: cutoff.toISOString().slice(0, 10) },
    };

    if (channelId) filter.channels = [channelId];

    return { filter };
  }, [channelId]);

  type StuckResult = { orders?: { totalCount?: number | null } | null };

  const { data: stuckData } = useQuery<StuckResult, { filter: any }>(
    ANALYTICS_STUCK_ORDERS_QUERY,
    {
      variables: stuckVariables,
      skip: !isLive,
      notifyOnNetworkStatusChange: true,
    },
  );

  const [edges, setEdges] = React.useState<NonNullable<AnalyticsOrdersQueryResult["orders"]>["edges"]>([] as any);
  const [paging, setPaging] = React.useState(false);
  const fetchedAllRef = React.useRef<string | null>(null);
  const [edgesAll, setEdgesAll] = React.useState<NonNullable<AnalyticsOrdersQueryResult["orders"]>["edges"]>([] as any);
  const [pagingAll, setPagingAll] = React.useState(false);
  const fetchedAllAllRef = React.useRef<string | null>(null);
  const [variantEdges, setVariantEdges] = React.useState<NonNullable<VariantsQueryResult["productVariants"]>["edges"]>([] as any);
  const [pagingVariants, setPagingVariants] = React.useState(false);
  const fetchedAllVariantsRef = React.useRef<string | null>(null);

  // Reset when inputs change
  React.useEffect(() => {
    setEdges([]);
    fetchedAllRef.current = null;
  }, [channelId, dateRange, isLive]);

  React.useEffect(() => {
    setEdgesAll([]);
    fetchedAllAllRef.current = null;
  }, [dateRange, isLive]);

  React.useEffect(() => {
    setVariantEdges([]);
    fetchedAllVariantsRef.current = null;
  }, [dateRange, isLive]);

  // Seed edges from initial page
  React.useEffect(() => {
    if (!isLive || !qData?.orders?.edges) return;

    if (fetchedAllRef.current === "done") return;

    setEdges(prev => {
      if (prev.length === 0) return qData.orders.edges;

      // Merge by unique node.id
      const existing = new Set(prev.map((e: any) => e.node.id));
      const next = qData.orders.edges.filter((e: any) => !existing.has(e.node.id));

      return [...prev, ...next];
    });
  }, [isLive, qData?.orders?.edges]);

  React.useEffect(() => {
    if (!isLive || !allData?.orders?.edges) return;

    if (fetchedAllAllRef.current === "done") return;

    setEdgesAll(prev => {
      if (prev.length === 0) return allData.orders.edges;

      const existing = new Set(prev.map((e: any) => e.node.id));
      const next = allData.orders.edges.filter((e: any) => !existing.has(e.node.id));

      return [...prev, ...next];
    });
  }, [isLive, allData?.orders?.edges]);

  // Seed variant edges
  React.useEffect(() => {
    if (!isLive || !vData?.productVariants?.edges) return;

    if (fetchedAllVariantsRef.current === "done") return;

    setVariantEdges(prev => {
      if (prev.length === 0) return vData.productVariants!.edges;

      const existing = new Set(prev.map((e: any) => e.node.id));
      const next = vData.productVariants!.edges.filter((e: any) => !existing.has(e.node.id));

      return [...prev, ...next];
    });
  }, [isLive, vData?.productVariants?.edges]);

  // Paginate until all pages are fetched
  React.useEffect(() => {
    if (!isLive) return;

    const pageInfo = qData?.orders?.pageInfo;

    if (!pageInfo || skip) return;

    if (!pageInfo.hasNextPage) {
      fetchedAllRef.current = "done";

      return;
    }

    let cancelled = false;
    const run = async () => {
      if (paging) return;

      setPaging(true);

      try {
        let after = pageInfo.endCursor;

        // Fetch subsequent pages in a loop
        while (!cancelled && after) {
          const res = await fetchMore({ variables: { after } });
          const nextPage = res?.data?.orders;
          const newEdges = nextPage?.edges ?? [];

          setEdges(prev => {
            const existing = new Set(prev.map((e: any) => e.node.id));
            const filtered = newEdges.filter((e: any) => !existing.has(e.node.id));

            return [...prev, ...filtered];
          });

          if (!nextPage?.pageInfo?.hasNextPage) break;

          after = nextPage.pageInfo.endCursor;
        }

        fetchedAllRef.current = "done";
      } finally {
        if (!cancelled) setPaging(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [qData?.orders?.pageInfo, fetchMore, skip, paging, isLive]);

  React.useEffect(() => {
    if (!isLive) return;

    const pageInfo = allData?.orders?.pageInfo;

    if (!pageInfo) return;

    if (!pageInfo.hasNextPage) {
      fetchedAllAllRef.current = "done";

      return;
    }

    let cancelled = false;
    const run = async () => {
      if (pagingAll) return;

      setPagingAll(true);

      try {
        let after = pageInfo.endCursor;

        while (!cancelled && after) {
          const res = await fetchMoreAll({ variables: { after } });
          const nextPage = res?.data?.orders;
          const newEdges = nextPage?.edges ?? [];

          setEdgesAll(prev => {
            const existing = new Set(prev.map((e: any) => e.node.id));
            const filtered = newEdges.filter((e: any) => !existing.has(e.node.id));

            return [...prev, ...filtered];
          });

          if (!nextPage?.pageInfo?.hasNextPage) break;

          after = nextPage.pageInfo.endCursor;
        }

        fetchedAllAllRef.current = "done";
      } finally {
        if (!cancelled) setPagingAll(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [allData?.orders?.pageInfo, fetchMoreAll, pagingAll, isLive]);

  // Paginate variants
  React.useEffect(() => {
    if (!isLive) return;

    const pageInfo = vData?.productVariants?.pageInfo;

    if (!pageInfo) return;

    if (!pageInfo.hasNextPage) {
      fetchedAllVariantsRef.current = "done";

      return;
    }

    let cancelled = false;
    const run = async () => {
      if (pagingVariants) return;

      setPagingVariants(true);

      try {
        let after = pageInfo.endCursor;

        while (!cancelled && after) {
          const res = await fetchMoreVariants({ variables: { after } });
          const nextPage = res?.data?.productVariants;
          const newEdges = nextPage?.edges ?? [];

          setVariantEdges(prev => {
            const existing = new Set(prev.map((e: any) => e.node.id));
            const filtered = newEdges.filter((e: any) => !existing.has(e.node.id));

            return [...prev, ...filtered];
          });

          if (!nextPage?.pageInfo?.hasNextPage) break;

          after = nextPage.pageInfo.endCursor;
        }

        fetchedAllVariantsRef.current = "done";
      } finally {
        if (!cancelled) setPagingVariants(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [vData?.productVariants?.pageInfo, fetchMoreVariants, pagingVariants, isLive]);

  const orders: Order[] = React.useMemo(() => {
    if (!isLive) {
      return generateMockOrders(dateRange, channelId);
    }

    const sourceEdges = edges.length > 0 ? edges : (qData?.orders?.edges ?? []);

    return sourceEdges.map((e: any) => {
      const n = e.node;

      return {
        id: n.id,
        created: new Date(n.created).toISOString().slice(0, 10),
        channelId: n.channel?.id ?? "",
        status: n.status,
        gross: n.total?.gross?.amount ?? 0,
      } as Order;
    });
  }, [isLive, edges, qData?.orders?.edges, dateRange, channelId]);

  // Phase 2 live metrics computed from order lines
  const { bestSellers, salesByCategory, topCustomers } = React.useMemo(() => {
    if (!isLive) {
      return { bestSellers: [], salesByCategory: [], topCustomers: [] };
    }

    const accProducts = new Map<string, { name: string; units: number; gmv: number }>();
    const accCategories = new Map<string, { category: string; gmv: number }>();
    const accCustomers = new Map<string, { customer: string; orders: number; gmv: number }>();

    const allEdges = edges.length > 0 ? edges : (qData?.orders?.edges ?? []);

    for (const e of allEdges) {
      const n = e.node;
      const orderGmv = n?.total?.gross?.amount ?? 0;
      const custKey = n?.userEmail || "Guest";

      // Top customers (by orders count and GMV)
      let cust = accCustomers.get(custKey);

      if (!cust) {
        cust = { customer: custKey, orders: 0, gmv: 0 };
        accCustomers.set(custKey, cust);
      }

      cust.orders += 1;
      cust.gmv = +(cust.gmv + orderGmv).toFixed(2);

      // Product/category aggregations from lines
      for (const line of n?.lines ?? []) {
        const qty = line?.quantity ?? 0;
        const lineGmv = line?.totalPrice?.gross?.amount ?? 0;
        const productName = line?.productName || line?.variant?.product?.name || "Product";
        const productKey = productName;
        const categoryName = line?.variant?.product?.category?.name || "Uncategorized";
        const categoryKey = categoryName;

        // Best sellers by product
        const prod = accProducts.get(productKey) || { name: productName, units: 0, gmv: 0 };

        prod.units += qty;
        prod.gmv = +(prod.gmv + lineGmv).toFixed(2);
        accProducts.set(productKey, prod);

        // Sales by category
        const cat = accCategories.get(categoryKey) || { category: categoryName, gmv: 0 };

        cat.gmv = +(cat.gmv + lineGmv).toFixed(2);
        accCategories.set(categoryKey, cat);
      }
    }

    const bestSellers: TopProduct[] = Array.from(accProducts.values()).sort((a, b) => b.units - a.units);
    const salesByCategory: CategorySales[] = Array.from(accCategories.values()).sort((a, b) => b.gmv - a.gmv);
    const topCustomers: TopCustomer[] = Array.from(accCustomers.values()).sort((a, b) => b.gmv - a.gmv);

    return { bestSellers, salesByCategory, topCustomers };
  }, [isLive, edges, qData?.orders?.edges]);

  // Live computations for additional metrics
  const liveMetrics = React.useMemo(() => {
    if (!isLive) {
      return {
        voucherImpact: 0,
        refundAmount: 0,
        giftCardImpact: 0,
        netSales: 0,
        refundRate: 0,
        repeatRate: 0,
        timeToSecondOrderDays: 0,
        avgFulfillmentHours: 0,
        onTimeFulfillmentRate: 0,
        salesByChannel: [] as CategorySales[],
        channelPerformance: [] as any[],
      };
    }

    const selectedEdges = edges.length > 0 ? edges : (qData?.orders?.edges ?? []);
    let voucherImpact = 0;
    let refundAmount = 0;
    let giftCardImpact = 0; // If API does not expose amount, remains 0
    let shippingRevenue = 0;

    // Customers grouping for repeat rate and time to 2nd order
    const customerOrders = new Map<string, string[]>();

    // Fulfillment timings
    const fulfillHours: number[] = [];
    const SLA_HOURS = 96;
    let onTimeCount = 0;

    for (const e of selectedEdges) {
      const n = e.node as any;

      // Discounts
      for (const d of n.discounts ?? []) {
        if (d?.type === "VOUCHER") {
          voucherImpact += d?.amount?.amount ?? 0;
        }
      }
      // Refunds
      refundAmount += n?.totalRefunded?.amount ?? 0;
      // Shipping revenue
      shippingRevenue += n?.shippingPrice?.gross?.amount ?? 0;

      // Customers
      const custKey = n?.user?.id || n?.userEmail || "Guest";
      const arr = customerOrders.get(custKey) || [];

      arr.push(n.created);
      customerOrders.set(custKey, arr);

      // Fulfillment (only fully fulfilled orders)
      if (n?.status === "FULFILLED") {
        const firstFulfill = (n?.fulfillments ?? [])
          .map((f: any) => new Date(f.created).getTime())
          .filter(Boolean)
          .sort((a: number, b: number) => a - b)[0];

        if (firstFulfill) {
          const createdTs = new Date(n.created).getTime();
          const hours = (firstFulfill - createdTs) / (1000 * 60 * 60);

          fulfillHours.push(hours);

          if (hours <= SLA_HOURS) onTimeCount += 1;
        }
      }
    }

    const gmv = (selectedEdges ?? []).reduce((sum, e: any) => sum + (e.node?.total?.gross?.amount ?? 0), 0);
    const refundRate = gmv > 0 ? refundAmount / gmv : 0;
    const netSales = Math.max(0, gmv - refundAmount - voucherImpact - giftCardImpact);

    // Repeat purchase rate & time to second order (in-range only)
    let repNumerator = 0;
    let repDenominator = 0;
    let totalDaysToSecond = 0;
    let secondCount = 0;

    for (const [_, arr] of customerOrders) {
      const sorted = arr.map(d => new Date(d).getTime()).sort((a, b) => a - b);

      if (sorted.length >= 1) repDenominator += 1;

      if (sorted.length >= 2) {
        repNumerator += 1;

        const days = (sorted[1] - sorted[0]) / (1000 * 60 * 60 * 24);

        totalDaysToSecond += days;
        secondCount += 1;
      }
    }

    const repeatRate = repDenominator > 0 ? repNumerator / repDenominator : 0;
    const timeToSecondOrderDays = secondCount > 0 ? totalDaysToSecond / secondCount : 0;

    // Fulfillment averages
    const avgFulfillmentHours = fulfillHours.length > 0 ? fulfillHours.reduce((a, b) => a + b, 0) / fulfillHours.length : 0;
    const onTimeFulfillmentRate = fulfillHours.length > 0 ? onTimeCount / fulfillHours.length : 0;

    // Sales by Channel/Currency across all channels
    const allEdges = edgesAll.length > 0 ? edgesAll : (allData?.orders?.edges ?? []);
    const byChannel = new Map<string, number>();
    const byChannelOrders = new Map<string, number>();
    const byChannelRefunded = new Map<string, number>();
    const labels = new Map<string, { channel: string; channelId?: string; currency: string }>();

    for (const e of allEdges) {
      const n = e.node as any;
      const key = `${n?.channel?.name || ""}__${n?.channel?.currencyCode || n?.total?.gross?.currency || ""}`;
      const amount = n?.total?.gross?.amount ?? 0;
      const refunded = n?.totalRefunded?.amount ?? 0;

      byChannel.set(key, (byChannel.get(key) ?? 0) + amount);
      byChannelOrders.set(key, (byChannelOrders.get(key) ?? 0) + 1);
      byChannelRefunded.set(key, (byChannelRefunded.get(key) ?? 0) + refunded);
      labels.set(key, {
        channel: n?.channel?.name || "",
        channelId: n?.channel?.id || undefined,
        currency: n?.channel?.currencyCode || n?.total?.gross?.currency || "",
      });
    }

    const salesByChannel = Array.from(byChannel.entries()).map(([key, gmv]) => {
      const l = labels.get(key)!;

      return { channel: l.channel, currency: l.currency, gmv };
    });

    const totalChannelGmv = salesByChannel.reduce((s, x) => s + x.gmv, 0) || 1;
    const channelPerformance = Array.from(byChannel.entries()).map(([key, gmv]) => {
      const l = labels.get(key)!;
      const orders = byChannelOrders.get(key) ?? 0;
      const refunded = byChannelRefunded.get(key) ?? 0;

      return {
        channel: l.channel,
        channelId: l.channelId,
        currency: l.currency,
        gmv,
        orders,
        aov: orders > 0 ? +(gmv / orders).toFixed(2) : 0,
        share: +(gmv / totalChannelGmv).toFixed(4),
        refundRate: gmv > 0 ? +(refunded / gmv).toFixed(4) : 0,
      };
    }).sort((a, b) => b.gmv - a.gmv);

    const result = {
      voucherImpact,
      refundAmount,
      giftCardImpact,
      netSales,
      refundRate,
      repeatRate,
      timeToSecondOrderDays,
      avgFulfillmentHours,
      onTimeFulfillmentRate,
      salesByChannel,
      channelPerformance,
    } as const;

    return result;
  }, [isLive, edges, qData?.orders?.edges, edgesAll, allData?.orders?.edges]);

  // Funnel + customer segments + cohorts (live only).
  // Uses already-fetched order edges + checkout count.
  const liveExtras = React.useMemo(() => {
    if (!isLive) {
      return {
        funnel: [] as Array<{ key: string; label: string; count: number }>,
        segments: { newCustomers: 0, returningCustomers: 0, newGmv: 0, returningGmv: 0 },
        cohorts: [] as Array<{ cohort: string; size: number; repeatRate: number; avgOrders: number; avgGmv: number }>,
        ordersHeatmap: Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0)),
        discountByDay: [] as Array<{ date: string; gmv: number; discount: number; pct: number }>,
        topDiscountedOrders: [] as Array<{ id: string; number: string; customer: string; total: number; discount: number; pct: number }>,
      };
    }

    const orderEdges = edges.length > 0 ? edges : (qData?.orders?.edges ?? []);
    const checkoutsCount = cData?.checkouts?.totalCount ?? 0;

    let ordersPlaced = 0;
    let fulfilledOrders = 0;

    // First pass: count orders per customer within the fetched window.
    // We use this to classify each order as "from new" or "from returning" buyer
    // based purely on activity in this window (no extra fields required).
    const customerOrderCount = new Map<string, number>();
    const customerFirstOrderDate = new Map<string, string>();

    for (const e of orderEdges) {
      const n = (e as any).node;
      const userId = n?.user?.id || n?.userEmail || "";

      if (!userId) continue;

      customerOrderCount.set(userId, (customerOrderCount.get(userId) ?? 0) + 1);

      const created = String(n?.created ?? "");
      const existing = customerFirstOrderDate.get(userId);

      if (!existing || created < existing) {
        customerFirstOrderDate.set(userId, created);
      }
    }

    // segments
    let newCustomers = 0;
    let returningCustomers = 0;
    let newGmv = 0;
    let returningGmv = 0;

    // cohorts: bucket customers by month of their *first observed order* in range.
    const cohortAcc = new Map<
      string,
      { customers: Set<string>; orders: Map<string, number>; gmv: Map<string, number> }
    >();

    // Heatmap: 7 rows (Mon..Sun) × 24 cols (0..23). Uses local browser time.
    const ordersHeatmap: number[][] = Array.from({ length: 7 }, () =>
      Array.from({ length: 24 }, () => 0),
    );

    // Discount tracking
    const discByDay = new Map<string, { gmv: number; discount: number }>();
    const ordersWithDiscount: Array<{ id: string; number: string; customer: string; total: number; discount: number; pct: number }> = [];

    for (const e of orderEdges) {
      const n = (e as any).node;
      const status = n?.status as string | undefined;
      const gmv = n?.total?.gross?.amount ?? 0;
      const userId = n?.user?.id || n?.userEmail || "";

      // Total discount on this order (sum of all discount lines)
      let orderDiscount = 0;

      for (const d of n?.discounts ?? []) {
        orderDiscount += d?.amount?.amount ?? 0;
      }

      const day = String(n?.created ?? "").slice(0, 10);

      if (day) {
        const acc = discByDay.get(day) ?? { gmv: 0, discount: 0 };

        acc.gmv += gmv;
        acc.discount += orderDiscount;
        discByDay.set(day, acc);
      }

      if (orderDiscount > 0 && gmv > 0) {
        ordersWithDiscount.push({
          id: n?.id ?? "",
          number: n?.number ?? "",
          customer: n?.userEmail || "Guest",
          total: gmv,
          discount: orderDiscount,
          pct: orderDiscount / (gmv + orderDiscount),
        });
      }

      // Heatmap (count every order, regardless of status)
      const createdRaw = n?.created;

      if (createdRaw) {
        const d = new Date(createdRaw);

        if (!isNaN(d.getTime())) {
          // JS getDay: Sun=0..Sat=6. Convert to Mon=0..Sun=6.
          const dow = (d.getDay() + 6) % 7;
          const hr = d.getHours();

          ordersHeatmap[dow][hr] += 1;
        }
      }

      if (status === "UNFULFILLED" || status === "PARTIALLY_FULFILLED" || status === "FULFILLED") {
        ordersPlaced += 1;
      }

      if (status === "FULFILLED") {
        fulfilledOrders += 1;
      }

      // New vs returning (in-window proxy): customer with >1 order in this window is returning.
      const totalForCustomer = userId ? customerOrderCount.get(userId) ?? 1 : 1;

      if (userId && totalForCustomer > 1) {
        returningCustomers += 1;
        returningGmv += gmv;
      } else {
        newCustomers += 1;
        newGmv += gmv;
      }

      // Cohort by month of first observed order in range.
      if (userId) {
        const firstOrderDate = customerFirstOrderDate.get(userId);

        if (firstOrderDate) {
          const cohort = firstOrderDate.slice(0, 7); // YYYY-MM
          let bucket = cohortAcc.get(cohort);

          if (!bucket) {
            bucket = { customers: new Set(), orders: new Map(), gmv: new Map() };
            cohortAcc.set(cohort, bucket);
          }

          bucket.customers.add(userId);
          bucket.orders.set(userId, (bucket.orders.get(userId) ?? 0) + 1);
          bucket.gmv.set(userId, (bucket.gmv.get(userId) ?? 0) + gmv);
        }
      }
    }

    // Funnel without chargeStatus: Checkouts → Orders → Fulfilled.
    const funnel: Array<{ key: string; label: string; count: number }> = [
      { key: "checkouts", label: "Checkouts started", count: checkoutsCount },
      { key: "orders", label: "Orders placed", count: ordersPlaced },
      { key: "fulfilled", label: "Fulfilled", count: fulfilledOrders },
    ];

    const cohorts = Array.from(cohortAcc.entries())
      .map(([cohort, b]) => {
        const size = b.customers.size;
        let repeats = 0;
        let totalOrders = 0;
        let totalGmv = 0;

        for (const id of b.customers) {
          const o = b.orders.get(id) ?? 0;
          const g = b.gmv.get(id) ?? 0;

          totalOrders += o;
          totalGmv += g;

          if (o > 1) repeats += 1;
        }

        return {
          cohort,
          size,
          repeatRate: size > 0 ? repeats / size : 0,
          avgOrders: size > 0 ? +(totalOrders / size).toFixed(2) : 0,
          avgGmv: size > 0 ? +(totalGmv / size).toFixed(2) : 0,
        };
      })
      .sort((a, b) => (a.cohort < b.cohort ? 1 : -1)) // newest first
      .slice(0, 6);

    const discountByDay = Array.from(discByDay.entries())
      .map(([date, v]) => ({
        date,
        gmv: +v.gmv.toFixed(2),
        discount: +v.discount.toFixed(2),
        pct: v.gmv + v.discount > 0 ? +(v.discount / (v.gmv + v.discount)).toFixed(4) : 0,
      }))
      .sort((a, b) => (a.date < b.date ? -1 : 1));

    const topDiscountedOrders = ordersWithDiscount
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 5);

    return {
      funnel,
      segments: { newCustomers, returningCustomers, newGmv, returningGmv },
      cohorts,
      ordersHeatmap,
      discountByDay,
      topDiscountedOrders,
    };
  }, [isLive, edges, qData?.orders?.edges, cData?.checkouts?.totalCount]);

  // Real compare-to-prior-period deltas (relative change: (current - prior) / prior).
  const comparison = React.useMemo(() => {
    const empty = {
      gmvDelta: null,
      ordersDelta: null,
      aovDelta: null,
      refundRateDelta: null,
      customersDelta: null,
      checkoutsDelta: null,
      priorRange,
    };

    if (!isLive) return empty;

    const priorEdges = priorData?.orders?.edges ?? [];
    const priorOrdersCount = priorData?.orders?.totalCount ?? priorEdges.length;
    let priorGmv = 0;
    let priorRefunded = 0;
    const priorCustomers = new Set<string>();

    for (const e of priorEdges) {
      const n = (e as any).node;
      const status = n?.status;

      if (status === "UNFULFILLED" || status === "PARTIALLY_FULFILLED" || status === "FULFILLED") {
        priorGmv += n?.total?.gross?.amount ?? 0;
      }

      priorRefunded += n?.totalRefunded?.amount ?? 0;

      const u = n?.user?.id || n?.userEmail || "";

      if (u) priorCustomers.add(u);
    }

    const priorAov = priorOrdersCount > 0 ? priorGmv / priorOrdersCount : 0;
    const priorRefundRate = priorGmv > 0 ? priorRefunded / priorGmv : 0;
    const priorCheckoutsCount = priorCheckoutsData?.checkouts?.totalCount ?? 0;

    // Current values come from already-fetched edges (no extra cost).
    const currentEdges = edges.length > 0 ? edges : (qData?.orders?.edges ?? []);
    let curGmv = 0;
    let curOrdersCount = 0;
    let curRefunded = 0;
    const curCustomers = new Set<string>();

    for (const e of currentEdges) {
      const n = (e as any).node;
      const status = n?.status;

      if (status === "UNFULFILLED" || status === "PARTIALLY_FULFILLED" || status === "FULFILLED") {
        curGmv += n?.total?.gross?.amount ?? 0;
        curOrdersCount += 1;
      }

      curRefunded += n?.totalRefunded?.amount ?? 0;

      const u = n?.user?.id || n?.userEmail || "";

      if (u) curCustomers.add(u);
    }

    const curAov = curOrdersCount > 0 ? curGmv / curOrdersCount : 0;
    const curRefundRate = curGmv > 0 ? curRefunded / curGmv : 0;
    const curCheckoutsCount = cData?.checkouts?.totalCount ?? 0;

    const ratio = (cur: number, prev: number) => (prev > 0 ? cur / prev - 1 : null);

    return {
      gmvDelta: ratio(curGmv, priorGmv),
      ordersDelta: ratio(curOrdersCount, priorOrdersCount),
      aovDelta: ratio(curAov, priorAov),
      refundRateDelta: priorRefundRate > 0 ? curRefundRate - priorRefundRate : null,
      customersDelta: ratio(curCustomers.size, priorCustomers.size),
      checkoutsDelta: ratio(curCheckoutsCount, priorCheckoutsCount),
      priorRange,
    };
  }, [
    isLive,
    priorData?.orders,
    priorCheckoutsData?.checkouts?.totalCount,
    edges,
    qData?.orders?.edges,
    cData?.checkouts?.totalCount,
    priorRange,
  ]);

  // Inventory metrics (low-stock, out-of-stock, DOH, dead stock)
  const inventoryMetrics = React.useMemo(() => {
    if (!isLive) {
      return { lowStockThreshold: 10, lowStock: [], outOfStockSkus: 0, doh: [], deadStock: [] };
    }

    const LOW = 10;
    // Availability by SKU from variants
    const availBySku = new Map<string, number>();
    const variants = variantEdges.length > 0 ? variantEdges : (vData?.productVariants?.edges ?? []);

    for (const e of variants) {
      const n = e.node as any;
      const sku = n?.sku || undefined;

      if (!sku) continue;

      if (n?.trackInventory === false) continue; // ignore untracked

      const stocks = n?.stocks ?? [];
      const available = stocks.reduce((sum: number, s: any) => sum + Math.max(0, (s?.quantity ?? 0) - (s?.quantityAllocated ?? 0)), 0);

      availBySku.set(sku, available);
    }

    // Units sold per SKU from orders in range
    const soldBySku = new Map<string, number>();
    const allEdges = edges.length > 0 ? edges : (qData?.orders?.edges ?? []);

    for (const e of allEdges) {
      const n = e.node as any;

      for (const l of n?.lines ?? []) {
        const sku = l?.variant?.sku as string | undefined;
        const qty = l?.quantity ?? 0;

        if (!sku) continue;

        soldBySku.set(sku, (soldBySku.get(sku) ?? 0) + qty);
      }
    }

    // Days in selected range
    const start = new Date(dateRange.from + "T00:00:00Z");
    const end = new Date(dateRange.to + "T00:00:00Z");
    const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);

    // Low stock items
    const lowStock: { variant: string; available: number }[] = [];
    let outOfStockSkus = 0;

    for (const [sku, available] of availBySku.entries()) {
      if (available <= 0) outOfStockSkus += 1;

      if (available <= LOW && available > 0) {
        lowStock.push({ variant: sku, available });
      }
    }
    lowStock.sort((a, b) => a.available - b.available);

    // DOH per SKU — only meaningful for SKUs that actually sold in the range,
    // and only useful for stockout risk (lowest days first).
    const doh = Array.from(availBySku.entries())
      .map(([sku, available]) => {
        const unitsSold = soldBySku.get(sku) ?? 0;
        const daily = unitsSold / days;
        const daysOnHand = daily > 0 ? Math.round(available / daily) : null;

        return { sku, days: daysOnHand, available, unitsSold };
      })
      .filter(d => d.days !== null && d.days >= 0)
      .sort((a, b) => (a.days as number) - (b.days as number))
      .slice(0, 50)
      .map(d => ({ sku: d.sku, days: d.days as number }));

    // Dead stock: stock available but zero sales in range. Order by on-hand qty
    // descending — most capital tied up first. Surface the units, not a date.
    const deadStock = Array.from(availBySku.entries())
      .filter(([sku, available]) => available > 0 && (soldBySku.get(sku) ?? 0) === 0)
      .sort((a, b) => b[1] - a[1])
      .map(([sku, available]) => ({ sku, days: available }))
      .slice(0, 50);

    return { lowStockThreshold: LOW, lowStock, outOfStockSkus, doh, deadStock };
  }, [isLive, variantEdges, vData?.productVariants?.edges, edges, qData?.orders?.edges, dateRange]);

  const data: AnalyticsResult = React.useMemo(() => {
    try {
      const base = aggregateAnalytics(orders, dateRange);

      // Only override with live-computed Phase 2 metrics when live is enabled
      if (isLive) {
        return {
          ...base,
          // Phase 1 live-backed
          bestSellers,
          salesByCategory,
          topCustomers,
          // Phase 2 live-backed
          salesByChannel: liveMetrics.salesByChannel as any,
          channelPerformance: liveMetrics.channelPerformance as any,
          netSales: liveMetrics.netSales,
          voucherImpact: liveMetrics.voucherImpact,
          giftCardImpact: liveMetrics.giftCardImpact,
          refundRate: liveMetrics.refundRate,
          repeatRate: liveMetrics.repeatRate,
          timeToSecondOrderDays: liveMetrics.timeToSecondOrderDays,
          avgFulfillmentHours: liveMetrics.avgFulfillmentHours,
          onTimeFulfillmentRate: liveMetrics.onTimeFulfillmentRate,
          // Inventory live-backed
          lowStockThreshold: inventoryMetrics.lowStockThreshold,
          lowStock: inventoryMetrics.lowStock,
          outOfStockSkus: inventoryMetrics.outOfStockSkus,
          doh: inventoryMetrics.doh,
          deadStock: inventoryMetrics.deadStock,
          // Funnel + segments + cohorts (live)
          funnel: liveExtras.funnel,
          segments: liveExtras.segments,
          cohorts: liveExtras.cohorts,
          ordersHeatmap: liveExtras.ordersHeatmap,
          discountByDay: liveExtras.discountByDay,
          topDiscountedOrders: liveExtras.topDiscountedOrders,
          comparison,
          stuckOrdersCount: stuckData?.orders?.totalCount ?? 0,
          stuckOrdersThresholdDays: STUCK_DAYS,
        } as AnalyticsResult;
      }

      return base;
    } catch (e) {
      // Fallback to empty aggregation on error
      const base = aggregateAnalytics([], dateRange);

      return base;
    }
  }, [
    orders,
    dateRange,
    isLive,
    bestSellers,
    salesByCategory,
    topCustomers,
    liveMetrics,
    inventoryMetrics,
    liveExtras,
    comparison,
    stuckData?.orders?.totalCount,
  ]);

return {
  data,
  loading: isLive
    ? qLoading || paging || loadingAll || pagingAll || loadingVariants || pagingVariants || loadingCheckouts || loadingPrior
    : false,
  // Optionally expose error for future UI handling
  // error,
} as const;
};
